(() => {
  const COMBO_CODE_KEY = 'yh_combo_discount';
  const COMBO_VARIANTS_KEY = 'yh_combo_variant_ids';

  const cartUpdateUrl = () => {
    const base = (window.routes && window.routes.cart_update_url) || '/cart/update';
    return base.endsWith('.js') ? base : `${base}.js`;
  };

  const cartJsonUrl = () => {
    const base = (window.routes && window.routes.cart_url) || '/cart';
    return base.endsWith('.js') ? base : `${base}.js`;
  };

  const rootPath = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const parseIdList = (value) =>
    String(value || '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);

  const getStoredCode = () => {
    try {
      return sessionStorage.getItem(COMBO_CODE_KEY) || '';
    } catch (_) {
      return '';
    }
  };

  const getRequiredVariantIds = () => {
    try {
      const stored = sessionStorage.getItem(COMBO_VARIANTS_KEY);
      if (stored) return parseIdList(stored);
    } catch (_) {
      /* ignore */
    }
    const root = document.querySelector('[data-yh-starter-kit]');
    return parseIdList(root?.dataset?.yhKitVariantIds);
  };

  const rememberComboConfig = (code, variantIds) => {
    try {
      if (code) sessionStorage.setItem(COMBO_CODE_KEY, code);
      if (variantIds?.length) sessionStorage.setItem(COMBO_VARIANTS_KEY, variantIds.join(','));
    } catch (_) {
      /* private mode */
    }
  };

  const clearStoredComboCode = () => {
    try {
      sessionStorage.removeItem(COMBO_CODE_KEY);
    } catch (_) {
      /* ignore */
    }
  };

  /**
   * Combo qualifies when BOTH required variants are in the cart.
   * Other products are allowed — Shopify "Amount off products" only
   * discounts the two combo products, not the rest of the cart.
   */
  const cartQualifiesForCombo = (cart, requiredVariantIds) => {
    if (!cart || !requiredVariantIds.length) return false;
    const items = cart.items || [];
    if (!items.length) return false;

    const cartVariantIds = items.map((item) => String(item.variant_id));
    const required = requiredVariantIds.map(String);

    return required.every((id) => cartVariantIds.includes(id));
  };

  const codeIsApplied = (cart, code) => {
    if (!cart || !code) return false;
    const needle = String(code).toUpperCase();
    return (cart.discount_codes || []).some((entry) => {
      return String(entry.code || '').toUpperCase() === needle && entry.applicable !== false;
    });
  };

  const fetchCart = async () => {
    const response = await fetch(cartJsonUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return response.json();
  };

  const setCartDiscount = async (codeOrEmpty) => {
    const response = await fetch(cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ discount: codeOrEmpty || '' }),
    });
    if (!response.ok) return null;
    return response.json().catch(() => null);
  };

  const applyComboDiscount = async (code) => {
    if (!code) return false;
    const normalized = String(code).trim();
    if (!normalized) return false;

    let cart = await setCartDiscount(normalized);
    if (codeIsApplied(cart, normalized)) return true;

    try {
      const body = new URLSearchParams({ discount: normalized });
      const response = await fetch(cartUpdateUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: body.toString(),
      });
      if (response.ok) {
        cart = await response.json().catch(() => null);
        if (codeIsApplied(cart, normalized)) return true;
      }
    } catch (_) {
      /* next */
    }

    try {
      await fetch(`${rootPath()}discount/${encodeURIComponent(normalized)}`, {
        credentials: 'same-origin',
        redirect: 'follow',
      });
    } catch (_) {
      /* verify */
    }

    cart = await fetchCart();
    return codeIsApplied(cart, normalized);
  };

  const removeComboDiscount = async (code) => {
    const cart = await fetchCart();
    if (!cart) return false;
    if (!codeIsApplied(cart, code) && !(cart.discount_codes || []).length) {
      clearStoredComboCode();
      return true;
    }
    await setCartDiscount('');
    clearStoredComboCode();
    return true;
  };

  const refreshCartDrawer = async (cartEl) => {
    if (!cartEl) return;

    const sectionResponse = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
    const sectionText = await sectionResponse.text();
    const sectionDoc = new DOMParser().parseFromString(sectionText, 'text/html');
    const newDrawer = sectionDoc.querySelector('#CartDrawer');
    const currentDrawer = document.querySelector('#CartDrawer');

    if (newDrawer && currentDrawer) {
      currentDrawer.innerHTML = newDrawer.innerHTML;
    }

    try {
      const bubbleResponse = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`);
      const bubbleText = await bubbleResponse.text();
      const bubbleDoc = new DOMParser().parseFromString(bubbleText, 'text/html');
      const newContent = bubbleDoc.querySelector('.shopify-section');
      const iconTarget = document.getElementById('cart-icon-bubble');
      if (newContent && iconTarget) {
        iconTarget.innerHTML = newContent.innerHTML;
      }
    } catch (_) {
      /* non-blocking */
    }

    cartEl.classList.remove('is-empty');
    cartEl.querySelector('cart-drawer-items')?.classList.remove('is-empty');

    const overlay = cartEl.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.onclick = null;
      overlay.addEventListener('click', cartEl.close.bind(cartEl));
    }
  };

  let syncing = false;

  /** Apply code only for exact 2-item combo; otherwise strip it. */
  const syncComboDiscount = async ({ refreshDrawer = false } = {}) => {
    if (syncing) return { changed: false, qualified: false };
    syncing = true;

    try {
      const root = document.querySelector('[data-yh-starter-kit]');
      const code =
        (root?.dataset?.yhKitDiscountCode || '').trim() || getStoredCode();
      const fromRoot = parseIdList(root?.dataset?.yhKitVariantIds);
      const requiredIds = fromRoot.length ? fromRoot : getRequiredVariantIds();

      if (code && requiredIds.length) {
        rememberComboConfig(code, requiredIds);
      }

      if (!code || requiredIds.length < 2) {
        return { changed: false, qualified: false };
      }

      const cart = await fetchCart();
      if (!cart) return { changed: false, qualified: false };

      const qualifies = cartQualifiesForCombo(cart, requiredIds);
      const applied = codeIsApplied(cart, code);
      let changed = false;

      if (qualifies && !applied) {
        changed = await applyComboDiscount(code);
      } else if (!qualifies && applied) {
        await removeComboDiscount(code);
        changed = true;
      } else if (!qualifies) {
        clearStoredComboCode();
      }

      if (changed && refreshDrawer) {
        const cartEl = document.querySelector('cart-drawer');
        if (cartEl) await refreshCartDrawer(cartEl);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'starter-kit-sync' });
        }
      }

      return { changed, qualified: qualifies };
    } finally {
      syncing = false;
    }
  };

  const submitCartCheckout = ({ discountCode = '' } = {}) => {
    const root = rootPath();
    // Same-origin checkout keeps the current cart. ?discount= auto-applies the code
    // so customers do not type it in the checkout coupon box.
    if (discountCode) {
      window.location.href = `${root}checkout?discount=${encodeURIComponent(discountCode)}`;
      return;
    }

    const form = document.getElementById('CartDrawer-Form');
    if (form) {
      let checkoutInput = form.querySelector('input[name="checkout"]');
      if (!checkoutInput) {
        checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        form.appendChild(checkoutInput);
      }
      form.submit();
      return;
    }
    window.location.href = `${root}checkout`;
  };

  // Checkout: same cart session + auto-pass coupon when combo qualifies.
  document.addEventListener(
    'click',
    async (event) => {
      const btn = event.target.closest('#CartDrawer-Checkout, button[name="checkout"], input[name="checkout"]');
      if (!btn) return;

      const code =
        getStoredCode() ||
        document.querySelector('[data-yh-starter-kit]')?.dataset?.yhKitDiscountCode ||
        '';
      const requiredIds = getRequiredVariantIds();

      event.preventDefault();
      event.stopPropagation();

      try {
        await syncComboDiscount({ refreshDrawer: false });
      } catch (_) {
        /* continue to checkout */
      }

      const cart = await fetchCart();
      const qualifies = requiredIds.length >= 2 && cartQualifiesForCombo(cart, requiredIds);

      if (code && !qualifies) {
        await removeComboDiscount(code).catch(() => null);
        submitCartCheckout();
        return;
      }

      if (code && qualifies) {
        if (!codeIsApplied(cart, code)) {
          await applyComboDiscount(code).catch(() => null);
        }
        rememberComboConfig(code, requiredIds);
        submitCartCheckout({ discountCode: code });
        return;
      }

      submitCartCheckout();
    },
    true
  );

  const init = (root) => {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    const addBtn = root.querySelector('[data-yh-kit-add]');
    const labelEl = root.querySelector('[data-yh-kit-add-label]');
    const spinner = root.querySelector('[data-yh-kit-spinner]');
    const status = root.querySelector('[data-yh-kit-status]');
    if (!addBtn) return;

    const defaultLabel = labelEl?.textContent || 'Add starter kit to cart';
    const discountCode = (root.dataset.yhKitDiscountCode || '').trim();
    const requiredIds = parseIdList(root.dataset.yhKitVariantIds);
    rememberComboConfig(discountCode, requiredIds);

    const setStatus = (message, type = '') => {
      if (!status) return;
      status.textContent = message || '';
      status.classList.remove('is-error', 'is-success');
      if (type) status.classList.add(type);
    };

    const setLoading = (loading) => {
      addBtn.classList.toggle('is-loading', loading);
      addBtn.disabled = loading || addBtn.hasAttribute('data-permanently-disabled');
      spinner?.classList.toggle('hidden', !loading);
      if (labelEl) labelEl.textContent = loading ? 'Adding…' : defaultLabel;
    };

    addBtn.addEventListener('click', async () => {
      const variants = Array.from(root.querySelectorAll('[data-yh-kit-variant]:not([disabled])'))
        .map((input) => input.value)
        .filter(Boolean);

      if (!variants.length) {
        setStatus('Select kit products in the theme editor.', 'is-error');
        return;
      }

      setLoading(true);
      setStatus('');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const payload = {
        items: variants.map((id) => ({ id: Number(id), quantity: 1 })),
      };

      if (cart?.getSectionsToRender) {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
      }

      try {
        // Add combo items without clearing other products — 10% stays on combo lines only
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();

        if (!response.ok || data.status || data.errors) {
          throw new Error(data.description || data.message || 'Could not add kit to cart.');
        }

        rememberComboConfig(discountCode, requiredIds.length ? requiredIds : variants);
        const sync = await syncComboDiscount({ refreshDrawer: false });

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'starter-kit', cartData: data });
        }

        if (cart?.tagName === 'CART-DRAWER') {
          await refreshCartDrawer(cart);
          cart.setActiveElement?.(addBtn);
          cart.open(addBtn);
        } else if (cart?.renderContents && data.sections) {
          const renderPayload = Array.isArray(data.items)
            ? { ...(data.items[0] || {}), sections: data.sections }
            : data;
          cart.renderContents(renderPayload);
        } else if (cart?.open) {
          cart.open(addBtn);
        } else {
          window.location.href = window.routes.cart_url;
        }

        if (discountCode && sync.qualified) {
          setStatus('Combo added — 10% on combo items only (other products stay full price).', 'is-success');
        } else if (discountCode) {
          setStatus('Items added. 10% applies when both combo products are in the cart.', 'is-error');
        } else {
          setStatus('Starter kit added to your cart.', 'is-success');
        }
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (error) {
        setStatus(error.message || 'Could not add kit to cart.', 'is-error');
      } finally {
        setLoading(false);
      }
    });
  };

  const boot = () => {
    document.querySelectorAll('[data-yh-starter-kit]').forEach(init);
    // Fix carts that already have the code on a single item
    syncComboDiscount({ refreshDrawer: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const el = event.target?.querySelector?.('[data-yh-starter-kit]') || event.target;
    if (el?.matches?.('[data-yh-starter-kit]')) init(el);
  });

  document.addEventListener('cart:updated', () => {
    syncComboDiscount({ refreshDrawer: true });
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'starter-kit-sync') return;
      syncComboDiscount({ refreshDrawer: true });
    });
  }
})();
